import posthog from "posthog-js";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import { toUtcDateOnlyISO } from "../timesheets/timesheetDateUtils";
import { parseUtcFromApi } from "./attendanceDateUtils";
import {
  AttendanceClientDeviceType,
  AttendanceReportingPeriodMode,
  deriveChangeRequestListItemUiFields,
} from "./attendanceConstants";

const { Attendance, OfficeAttendanceSettings, AttendanceChangeRequests, Office } = config.REST_API;

/** BaseModel envelope → payload */
const unwrapEnvelope = (apiData) => apiData?.data ?? apiData?.Data ?? apiData;

export const assertAttendanceOk = (envelope) => {
  const ok = envelope?.success ?? envelope?.Success ?? true;
  if (ok === false) {
    const msg = envelope?.message ?? envelope?.Message ?? "Request failed";
    const err = new Error(msg);
    err.isAttendanceEnvelopeError = true;
    err.envelope = envelope;
    throw err;
  }
};

const unwrapInner = (apiData) => {
  const d = unwrapEnvelope(apiData);
  return d;
};

/**
 * Deserialize bool from API JSON (camelCase or PascalCase) without treating string `"false"` as true.
 * @param {unknown} value
 * @param {boolean} defaultValue
 */
const readApiBool = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
  }
  return defaultValue;
};

export const handleAttendanceApiError = (error, fallbackMessage) => {
  const msg =
    error?.response?.data?.message ??
    error?.response?.data?.Message ??
    error?.message ??
    (error?.isAttendanceEnvelopeError ? error.message : null) ??
    fallbackMessage;
  if (typeof posthog !== "undefined" && posthog.capture) {
    posthog.capture("attendanceApiError", {
      type: "exception",
      status: error?.response?.status,
      message: msg,
    });
  }
  Common.showErrorMessage(msg);
};

/** @param {unknown} raw — `AttendanceEventChangeRequestInfoDto` */
const normalizeAttendanceEventChangeRequestInfo = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    requestId: String(o.requestId ?? o.RequestId ?? ""),
    requestType: Number(o.requestType ?? o.RequestType ?? 0),
    status: Number(o.status ?? o.Status ?? 0),
    submittedAtUtc: o.submittedAtUtc ?? o.SubmittedAtUtc ?? null,
  };
};

/** @param {unknown} raw */
export const normalizeUserAttendanceEvent = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const crRaw = o.changeRequest ?? o.ChangeRequest;
  const changeRequest = normalizeAttendanceEventChangeRequestInfo(crRaw);
  const projectIdsRaw = o.projectIds ?? o.ProjectIds;
  /** @type {string[]} */
  let projectIds = [];
  if (Array.isArray(projectIdsRaw)) {
    projectIds = projectIdsRaw.map((x) => String(x).trim()).filter(Boolean);
  }
  const legacyProjectId = o.projectId ?? o.ProjectId ?? "";
  if (projectIds.length === 0 && legacyProjectId != null && String(legacyProjectId).trim() !== "") {
    projectIds = [String(legacyProjectId).trim()];
  }
  return {
    id: String(o.id ?? o.Id ?? o.eventId ?? o.EventId ?? ""),
    eventType: Number(o.eventType ?? o.EventType ?? 0),
    occurredAtUtc: o.occurredAtUtc ?? o.OccurredAtUtc ?? null,
    location: o.location ?? o.Location ?? "",
    note: o.note ?? o.Note ?? "",
    projectId: projectIds.length > 0 ? projectIds[0] : "",
    projectIds,
    clientDeviceType: Number(o.clientDeviceType ?? o.ClientDeviceType ?? 0),
    workplaceType: Number(o.workplaceType ?? o.WorkplaceType ?? 0),
    status: Number(o.status ?? o.Status ?? 0),
    changeRequest,
  };
};

/** @param {unknown} raw */
export const normalizeSegment = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const startUtc =
    o.startUtc ?? o.StartUtc ?? o.checkInUtc ?? o.CheckInUtc ?? null;
  const endUtc = o.endUtc ?? o.EndUtc ?? o.checkOutUtc ?? o.CheckOutUtc ?? null;
  const durRaw = o.durationHours ?? o.DurationHours;
  const durationHours =
    durRaw != null && durRaw !== "" && Number.isFinite(Number(durRaw)) ? Number(durRaw) : 0;
  return {
    durationHours,
    isOpen: Boolean(o.isOpen ?? o.IsOpen),
    startUtc,
    endUtc,
  };
};

/** @param {unknown} v */
const numOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalizes `Today` / `Day` responses (`TodayAttendanceActivityDto`).
 * @param {unknown} apiData
 */
export const normalizeDayActivity = (apiData) => {
  const d = unwrapInner(apiData);
  if (!d || typeof d !== "object") {
    return {
      dayStartUtc: null,
      dayEndUtc: null,
      events: [],
      segments: [],
      totalWorkedHours: null,
      overtimeHours: null,
      allowUsersToEditSubmittedEvents: false,
      allowUsersToDeleteSubmittedEvents: false,
      openCheckInSessionOnly: false,
      allowFlexibleWorkingWindow: false,
    };
  }
  const o = /** @type {Record<string, unknown>} */ (d);
  const eventsRaw = o.events ?? o.Events;
  const segmentsRaw = o.segments ?? o.Segments;
  return {
    dayStartUtc: o.dayStartUtc ?? o.DayStartUtc ?? null,
    dayEndUtc: o.dayEndUtc ?? o.DayEndUtc ?? null,
    events: Array.isArray(eventsRaw) ? eventsRaw.map(normalizeUserAttendanceEvent).filter(Boolean) : [],
    segments: Array.isArray(segmentsRaw) ? segmentsRaw.map(normalizeSegment).filter(Boolean) : [],
    totalWorkedHours: numOrNull(o.totalWorkedHours ?? o.TotalWorkedHours),
    overtimeHours: numOrNull(o.overtimeHours ?? o.OvertimeHours),
    allowUsersToEditSubmittedEvents: readApiBool(o.allowUsersToEditSubmittedEvents ?? o.AllowUsersToEditSubmittedEvents, false),
    allowUsersToDeleteSubmittedEvents: readApiBool(o.allowUsersToDeleteSubmittedEvents ?? o.AllowUsersToDeleteSubmittedEvents, false),
    /** True when that UTC day has exactly one row and it is a check-in (open session, no check-out yet). Direct edit off. */
    openCheckInSessionOnly: readApiBool(o.openCheckInSessionOnly ?? o.OpenCheckInSessionOnly, false),
    /** When true, fixed start time / punctuality does not apply (flexible working window). */
    allowFlexibleWorkingWindow: readApiBool(o.allowFlexibleWorkingWindow ?? o.AllowFlexibleWorkingWindow, false),
  };
};

/** @param {unknown} apiData */
export const normalizeTodayActivity = (apiData) => normalizeDayActivity(apiData);

export const sumClosedHours = (segments) => {
  if (!Array.isArray(segments)) return 0;
  return segments.reduce((acc, s) => acc + (Number(s?.durationHours) || 0), 0);
};

export const hasOpenSegment = (segments) =>
  Array.isArray(segments) && segments.some((s) => s && s.isOpen);

export const lastEvent = (events) => {
  if (!Array.isArray(events) || events.length === 0) return null;
  const sorted = [...events].sort((a, b) => {
    const ta = parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0;
    const tb = parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0;
    return ta - tb;
  });
  return sorted[sorted.length - 1];
};

/** Next action for primary button: check-in vs check-out */
export const inferNextClockEventType = (segments, events) => {
  if (hasOpenSegment(segments)) return 1; // CheckOut
  const le = lastEvent(events);
  if (!le) return 0;
  return le.eventType === 0 ? 1 : 0;
};

export const fetchTodayActivity = async () => {
  const res = await axiosBasic.get(Attendance.Today);
  assertAttendanceOk(res.data);
  return normalizeDayActivity(unwrapInner(res.data));
};

/**
 * Current user’s activity for a UTC calendar day (query `dateUtc` = ISO instant, e.g. start of civil day).
 * @param {string} dateUtcIso
 */
export const fetchDayActivity = async (dateUtcIso) => {
  const params = { dateUtc: dateUtcIso };
  try {
    const res = await axiosBasic.get(Attendance.Day, { params });
    assertAttendanceOk(res.data);
    return normalizeDayActivity(unwrapInner(res.data));
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) {
      const res = await axiosBasic.get(Attendance.GetDayActivity, { params });
      assertAttendanceOk(res.data);
      return normalizeDayActivity(unwrapInner(res.data));
    }
    throw e;
  }
};

/**
 * `HttpDelete Attendance/SelfDelete/{eventId}`
 * @param {string} eventId
 */
export const selfDeleteAttendanceEvent = async (eventId) => {
  const id = String(eventId ?? "").trim();
  if (!id) {
    const err = new Error("EventId required");
    err.isAttendanceValidationError = true;
    throw err;
  }
  const res = await axiosBasic.delete(`${Attendance.SelfDelete}/${encodeURIComponent(id)}`);
  if (res.status === 204 || res.data == null || res.data === "") {
    return null;
  }
  assertAttendanceOk(res.data);
  return unwrapInner(res.data);
};

/**
 * Builds `AttendanceSelfEditRequest` (PascalCase) for PUT SelfEdit.
 * @param {Record<string, unknown>} raw
 */
export const buildAttendanceSelfEditPayload = (raw) => {
  const o = raw && typeof raw === "object" ? raw : {};
  const eventId = o.eventId ?? o.EventId;
  /** @type {Record<string, unknown>} */
  const payload = {
    EventId: String(eventId ?? ""),
  };
  const occ = o.occurredAtUtc ?? o.OccurredAtUtc;
  if (occ instanceof Date) {
    payload.OccurredAtUtc = occ.toISOString();
  } else if (occ != null && String(occ).trim() !== "") {
    payload.OccurredAtUtc = String(occ);
  }
  const loc = o.location ?? o.Location;
  if (loc != null) payload.Location = String(loc);
  const note = o.note ?? o.Note;
  if (note != null) payload.Note = String(note);
  const projectIdsRaw = o.projectIds ?? o.ProjectIds;
  if (Array.isArray(projectIdsRaw)) {
    payload.ProjectIds = projectIdsRaw.map((x) => String(x).trim()).filter(Boolean);
    payload.ProjectId = payload.ProjectIds.length > 0 ? payload.ProjectIds[0] : "";
  } else if (Object.prototype.hasOwnProperty.call(o, "projectId") || Object.prototype.hasOwnProperty.call(o, "ProjectId")) {
    const projectRaw = o.projectId ?? o.ProjectId;
    payload.ProjectId = projectRaw != null ? String(projectRaw).trim() : "";
    payload.ProjectIds = payload.ProjectId ? [payload.ProjectId] : [];
  }
  const wt = o.workplaceType ?? o.WorkplaceType;
  if (wt !== undefined && wt !== null && wt !== "") {
    const n = Number(wt);
    if (Number.isFinite(n)) payload.WorkplaceType = n;
  }
  return payload;
};

/**
 * Self-service edit (`HttpPut Attendance/SelfEdit`). Returns normalized `UserAttendanceEventDto`.
 * @param {Record<string, unknown>} body — must include `eventId` / `EventId`; optional `occurredAtUtc`, `location`, `note`, `projectId`, `workplaceType`
 */
export const selfEditAttendanceEvent = async (body) => {
  const payload = buildAttendanceSelfEditPayload(body);
  if (!payload.EventId) {
    const err = new Error("EventId required");
    err.isAttendanceValidationError = true;
    throw err;
  }
  const res = await axiosBasic.put(Attendance.SelfEdit, payload);
  assertAttendanceOk(res.data);
  return normalizeUserAttendanceEvent(unwrapInner(res.data));
};

/**
 * Maps clock modal / hub data to `AttendanceClockRequest` (PascalCase for ASP.NET model binding).
 * @param {Record<string, unknown>} raw
 */
export const buildAttendanceClockRequestPayload = (raw) => {
  const o = raw && typeof raw === "object" ? raw : {};
  const eventType = Number(o.eventType ?? o.EventType ?? 0);
  const clientDeviceType = Number(o.clientDeviceType ?? o.ClientDeviceType ?? 0);
  const workplaceType = Number(o.workplaceType ?? o.WorkplaceType ?? 0);
  /** @type {Record<string, unknown>} */
  const payload = {
    EventType: eventType,
    ClientDeviceType: clientDeviceType,
    WorkplaceType: workplaceType,
  };
  const officeRaw = o.officeId ?? o.OfficeId;
  if (officeRaw != null && officeRaw !== "") {
    const oid = Number(officeRaw);
    if (Number.isFinite(oid) && oid > 0) payload.OfficeId = oid;
  }
  const deptRaw = o.departmentId ?? o.DepartmentId;
  if (deptRaw != null && deptRaw !== "") {
    const did = Number(deptRaw);
    if (Number.isFinite(did) && did > 0) payload.DepartmentId = did;
  }
  const projectIdsRaw = o.projectIds ?? o.ProjectIds;
  if (Array.isArray(projectIdsRaw)) {
    const ids = projectIdsRaw.map((x) => String(x).trim()).filter(Boolean);
    if (ids.length > 0) {
      payload.ProjectIds = ids;
      payload.ProjectId = ids[0];
    }
  } else {
    const projectRaw = o.projectId ?? o.ProjectId;
    if (projectRaw != null && String(projectRaw).trim() !== "") {
      const id = String(projectRaw).trim();
      payload.ProjectId = id;
      payload.ProjectIds = [id];
    }
  }
  const loc = o.location ?? o.Location;
  if (loc != null && String(loc).trim() !== "") {
    payload.Location = String(loc);
  }
  const note = o.note ?? o.Note;
  if (note != null && String(note).trim() !== "") {
    payload.Note = String(note);
  }
  return payload;
};

/**
 * @param {Record<string, unknown>} body
 */
export const clockAttendance = async (body) => {
  const payload = buildAttendanceClockRequestPayload(body);
  const res = await axiosBasic.post(Attendance.Clock, payload);
  assertAttendanceOk(res.data);
  const d = unwrapInner(res.data);
  return {
    createdEvent: normalizeUserAttendanceEvent(d?.createdEvent ?? d?.CreatedEvent),
    todayActivity: normalizeDayActivity(d?.todayActivity ?? d?.TodayActivity),
  };
};

/**
 * Builds `AttendanceChangeRequestSubmitDto` (PascalCase) for POST `Attendance/SubmitChangeRequest`.
 * @param {Record<string, unknown>} raw — camelCase or PascalCase; supports `occurredAtUtc` from self-edit modal for `PendingOccurredAtUtc`.
 */
export const buildAttendanceChangeRequestSubmitPayload = (raw) => {
  const o = raw && typeof raw === "object" ? raw : {};
  const eventId = o.eventId ?? o.EventId;
  const requestType = Number(o.requestType ?? o.RequestType ?? 0);
  /** @type {Record<string, unknown>} */
  const payload = {
    EventId: String(eventId ?? "").trim(),
    RequestType: requestType,
  };
  if (!payload.EventId) {
    const err = new Error("EventId required");
    err.isAttendanceValidationError = true;
    throw err;
  }
  const pendingOcc =
    o.pendingOccurredAtUtc ??
    o.PendingOccurredAtUtc ??
    o.occurredAtUtc ??
    o.OccurredAtUtc;
  if (pendingOcc != null && String(pendingOcc).trim() !== "") {
    payload.PendingOccurredAtUtc =
      pendingOcc instanceof Date ? pendingOcc.toISOString() : String(pendingOcc);
  }
  const pLoc = o.pendingLocation ?? o.PendingLocation ?? o.location ?? o.Location;
  if (pLoc !== undefined && pLoc !== null) {
    payload.PendingLocation = String(pLoc);
  }
  const pNote = o.pendingNote ?? o.PendingNote ?? o.note ?? o.Note;
  if (pNote !== undefined && pNote !== null) {
    payload.PendingNote = String(pNote);
  }
  const pProj = o.pendingProjectId ?? o.PendingProjectId ?? o.projectId ?? o.ProjectId;
  if (pProj !== undefined && pProj !== null) {
    payload.PendingProjectId = String(pProj).trim();
  }
  const pWt = o.pendingWorkplaceType ?? o.PendingWorkplaceType ?? o.workplaceType ?? o.WorkplaceType;
  if (pWt !== undefined && pWt !== null && pWt !== "") {
    const n = Number(pWt);
    if (Number.isFinite(n)) payload.PendingWorkplaceType = n;
  }
  return payload;
};

/** @param {unknown} raw */
const normalizeAttendanceChangeRequestSubmitAck = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    requestId: String(o.requestId ?? o.RequestId ?? ""),
    userAttendanceEventId: String(o.userAttendanceEventId ?? o.UserAttendanceEventId ?? ""),
    submittedAtUtc: o.submittedAtUtc ?? o.SubmittedAtUtc ?? null,
  };
};

/**
 * @param {Record<string, unknown>} body
 * @returns {Promise<{ requestId: string, userAttendanceEventId: string, submittedAtUtc: unknown } | null>}
 */
export const submitChangeRequest = async (body) => {
  const payload = buildAttendanceChangeRequestSubmitPayload(body);
  const res = await axiosBasic.post(Attendance.SubmitChangeRequest, payload);
  assertAttendanceOk(res.data);
  const inner = unwrapInner(res.data);
  return inner ? normalizeAttendanceChangeRequestSubmitAck(inner) : null;
};

export const fetchAttendanceReport = async (fromUtc, toUtc, officeId, departmentId) => {
  const params = {
    fromUtc: typeof fromUtc === "string" ? fromUtc : fromUtc.toISOString(),
    toUtc: typeof toUtc === "string" ? toUtc : toUtc.toISOString(),
  };
  if (officeId != null && officeId !== "") params.officeId = officeId;
  if (departmentId != null && departmentId !== "") params.departmentId = departmentId;
  const res = await axiosBasic.get(Attendance.Report, { params });
  assertAttendanceOk(res.data);
  const data = unwrapInner(res.data);
  return Array.isArray(data) ? data : [];
};

/** @param {unknown} row */
const mapReportRow = (row) => {
  if (!row || typeof row !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (row);
  return {
    userName: o.userName ?? o.UserName ?? "",
    email: o.email ?? o.Email ?? "",
    officeName: o.officeName ?? o.OfficeName ?? "",
    departmentName: o.departmentName ?? o.DepartmentName ?? "",
    day: o.day ?? o.Day ?? null,
    firstCheckIn: o.firstCheckIn ?? o.FirstCheckIn ?? null,
    lastCheckOut: o.lastCheckOut ?? o.LastCheckOut ?? null,
    workHours: o.workHours ?? o.WorkHours ?? null,
    statusLabel: o.statusLabel ?? o.StatusLabel ?? "",
  };
};

export const normalizeReportRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map(mapReportRow).filter(Boolean);
};

export const fetchAttendanceStats = async (fromUtc, toUtc) => {
  const params = {
    fromUtc: typeof fromUtc === "string" ? fromUtc : fromUtc.toISOString(),
    toUtc: typeof toUtc === "string" ? toUtc : toUtc.toISOString(),
  };
  const res = await axiosBasic.get(Attendance.Stats, { params });
  assertAttendanceOk(res.data);
  const d = unwrapInner(res.data);
  return d && typeof d === "object" ? d : {};
};

export const fetchAttendanceExportBlob = async (format, fromUtc, toUtc, officeId, departmentId) => {
  const params = {
    fromUtc: typeof fromUtc === "string" ? fromUtc : fromUtc.toISOString(),
    toUtc: typeof toUtc === "string" ? toUtc : toUtc.toISOString(),
    format: format || "xlsx",
  };
  if (officeId != null && officeId !== "") params.officeId = officeId;
  if (departmentId != null && departmentId !== "") params.departmentId = departmentId;
  const res = await axiosBasic.get(Attendance.ExportReport, {
    params,
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertAttendanceOk(json);
    } catch (e) {
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

/** Unwrap various API shapes to a plain array of office-setting rows */
export const extractOfficeSettingsArray = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "object") {
    const d = /** @type {Record<string, unknown>} */ (data);
    const inner = d.items ?? d.Items ?? d.data ?? d.Data ?? d.$values ?? d.Values;
    if (Array.isArray(inner)) return inner;
  }
  return [];
};

export const fetchOfficeAttendanceByCompany = async () => {
  const res = await axiosBasic.get(OfficeAttendanceSettings.ByCompany);
  assertAttendanceOk(res.data);
  const data = unwrapInner(res.data);
  return extractOfficeSettingsArray(data);
};

/**
 * Single-office attendance rules (for hub: location capture flag, etc.).
 * @param {number|string} officeId
 */
export const fetchOfficeAttendanceByOffice = async (officeId) => {
  const id = Number(officeId);
  if (!Number.isFinite(id) || id <= 0) return null;
  try {
    const res = await axiosBasic.get(OfficeAttendanceSettings.ByOffice, {
      params: { officeId: id },
    });
    assertAttendanceOk(res.data);
    const data = unwrapInner(res.data);
    return normalizeOfficeSettings(data);
  } catch {
    return null;
  }
};

/**
 * Whether check-in requires a successful device location (backend `RequireUserLocation`).
 * Defaults to false when settings are unknown.
 * @param {number|string|undefined|null} officeId
 */
export const getRequireUserLocationForOffice = async (officeId) => {
  const id = Number(officeId);
  if (!Number.isFinite(id) || id <= 0) return false;
  const one = await fetchOfficeAttendanceByOffice(id);
  if (one && Number(one.officeId) === id) {
    return one.requireUserLocation === true;
  }
  try {
    const rows = await fetchOfficeAttendanceByCompany();
    if (!Array.isArray(rows)) return false;
    for (const raw of rows) {
      const n = normalizeOfficeSettings(raw);
      if (n && Number(n.officeId) === id) return n.requireUserLocation === true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

/**
 * Company offices (for config UI when `/ByCompany` is empty or partial).
 * @returns {Promise<{ id: number, name: string }[]>}
 */
export const fetchCompanyOfficesForAttendance = async () => {
  const res = await axiosBasic.get(Office.GetCompanyOffices);
  const body = res.data || {};
  if (body.success === false) return [];
  const data = body.data ?? body.Data;
  if (!Array.isArray(data)) return [];
  return data
    .map((o) => {
      const id = Number(o.id ?? o.Id);
      const n = o.name ?? o.Name ?? "";
      const c = o.countryName ?? o.CountryName;
      const name = c ? `${n} (${c})` : String(n);
      return { id, name: name.trim() || `Office ${id}` };
    })
    .filter((o) => Number.isFinite(o.id) && o.id > 0);
};

/** Default row when no saved settings exist yet for an office */
export const defaultOfficeAttendanceRow = (officeId, officeName) => ({
  officeId,
  officeName: officeName || `Office ${officeId}`,
  workDayStart: "09:00:00",
  workDayEnd: "18:00:00",
  checkInGraceMinutes: 15,
  checkOutGraceMinutes: 0,
  allowFlexibleWorkingWindow: false,
  allowUsersToEditSubmittedEvents: false,
  allowUsersToDeleteSubmittedEvents: false,
  allowMultipleCheckInOutPerDay: true,
  /** When true, check-in requires device location (backend `RequireUserLocation`) */
  requireUserLocation: false,
  /** When true, biometric punches may create check-out events (backend `AllowBiometricCheckOut`) */
  allowBiometricCheckOut: true,
});

/**
 * Merge `Office/GetCompanyOffices` with `OfficeAttendanceSettings/ByCompany` so every office has a row.
 * @param {{ id: number, name: string }[]} offices
 * @param {unknown[]} settingsRows
 */
export const buildAttendanceConfigurationRows = (offices, settingsRows) => {
  const byId = new Map();
  for (const raw of settingsRows) {
    const n = normalizeOfficeSettings(raw);
    if (n && Number.isFinite(n.officeId) && n.officeId > 0) {
      byId.set(Number(n.officeId), n);
    }
  }
  if (!offices.length) {
    return Array.from(byId.values()).sort((a, b) => String(a.officeName).localeCompare(String(b.officeName), undefined, { sensitivity: "base" }));
  }
  return offices.map((o) => {
    const existing = byId.get(Number(o.id));
    if (existing) {
      return { ...existing, officeName: o.name || existing.officeName };
    }
    return defaultOfficeAttendanceRow(o.id, o.name);
  });
};

/** @param {unknown} raw */
export const normalizeOfficeSettings = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const nestedOffice = o.office ?? o.Office;
  const rawId =
    o.officeId ??
    o.OfficeId ??
    (nestedOffice && typeof nestedOffice === "object"
      ? /** @type {Record<string, unknown>} */ (nestedOffice).id ?? /** @type {Record<string, unknown>} */ (nestedOffice).Id
      : undefined);
  let officeId = 0;
  if (typeof rawId === "number" && Number.isFinite(rawId)) officeId = rawId;
  else if (typeof rawId === "string" && /^\d+$/.test(rawId.trim())) officeId = parseInt(rawId.trim(), 10);
  else officeId = Number(rawId) || 0;

  const name = o.officeName ?? o.OfficeName ?? (nestedOffice && typeof nestedOffice === "object" ? /** @type {Record<string, unknown>} */ (nestedOffice).name ?? /** @type {Record<string, unknown>} */ (nestedOffice).Name : "") ?? "";

  return {
    officeId,
    officeName: String(name ?? ""),
    workDayStart: o.workDayStart ?? o.WorkDayStart ?? "",
    workDayEnd: o.workDayEnd ?? o.WorkDayEnd ?? "",
    checkInGraceMinutes: Number(o.checkInGraceMinutes ?? o.CheckInGraceMinutes ?? 0) || 0,
    checkOutGraceMinutes: Number(o.checkOutGraceMinutes ?? o.CheckOutGraceMinutes ?? 0) || 0,
    /** C#: `bool AllowFlexibleWorkingWindow` — JSON often `allowFlexibleWorkingWindow` */
    allowFlexibleWorkingWindow: readApiBool(
      o.allowFlexibleWorkingWindow ?? o.AllowFlexibleWorkingWindow ?? o.flexibleWorkingWindow ?? o.FlexibleWorkingWindow,
      false
    ),
    allowUsersToEditSubmittedEvents: readApiBool(
      o.allowUsersToEditSubmittedEvents ?? o.AllowUsersToEditSubmittedEvents,
      false
    ),
    allowUsersToDeleteSubmittedEvents: readApiBool(
      o.allowUsersToDeleteSubmittedEvents ?? o.AllowUsersToDeleteSubmittedEvents,
      false
    ),
    allowMultipleCheckInOutPerDay: readApiBool(
      o.allowMultipleCheckInOutPerDay ?? o.AllowMultipleCheckInOutPerDay,
      true
    ),
    requireUserLocation: readApiBool(o.requireUserLocation ?? o.RequireUserLocation, false),
    allowBiometricCheckOut: readApiBool(o.allowBiometricCheckOut ?? o.AllowBiometricCheckOut, true),
  };
};

/** Time inputs from `<input type="time">` → `HH:mm:ss` for .NET TimeSpan binding */
const toApiTimeSpanString = (v) => {
  if (v == null || v === "") return "09:00:00";
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return "09:00:00";
  const h = m[1].padStart(2, "0");
  const min = m[2];
  const sec = (m[3] ?? "00").padStart(2, "0");
  return `${h}:${min}:${sec}`;
};

/**
 * Build JSON body for POST `OfficeAttendanceSettings/Save`.
 * Uses PascalCase property names so the server DTO binds (same pattern as `Company.UpdateSettings`).
 *
 * Maps to C# properties including `bool AllowFlexibleWorkingWindow { get; set; }`.
 */
export const buildOfficeAttendanceSettingsSavePayload = (body) => {
  const officeId = Number(body?.officeId);
  if (!Number.isFinite(officeId) || officeId <= 0) {
    const err = new Error("Invalid office id");
    err.code = "INVALID_OFFICE_ID";
    throw err;
  }
  return {
    OfficeId: officeId,
    WorkDayStart: toApiTimeSpanString(body.workDayStart),
    WorkDayEnd: toApiTimeSpanString(body.workDayEnd),
    CheckInGraceMinutes: Number(body.checkInGraceMinutes) || 0,
    CheckOutGraceMinutes: Number(body.checkOutGraceMinutes) || 0,
    AllowFlexibleWorkingWindow: readApiBool(body?.allowFlexibleWorkingWindow, false),
    AllowUsersToEditSubmittedEvents: readApiBool(body?.allowUsersToEditSubmittedEvents, false),
    AllowUsersToDeleteSubmittedEvents: readApiBool(body?.allowUsersToDeleteSubmittedEvents, false),
    AllowMultipleCheckInOutPerDay: readApiBool(body?.allowMultipleCheckInOutPerDay, true),
    RequireUserLocation: readApiBool(body?.requireUserLocation, false),
    AllowBiometricCheckOut: readApiBool(body?.allowBiometricCheckOut, true),
  };
};

export const saveOfficeAttendanceSettings = async (body) => {
  const payload = buildOfficeAttendanceSettingsSavePayload(body);
  const res = await axiosBasic.post(OfficeAttendanceSettings.Save, payload);
  assertAttendanceOk(res.data);
  const d = unwrapInner(res.data);
  return normalizeOfficeSettings(d);
};

/** @param {unknown} raw */
const normalizeAttendanceChangeRequestPageStatistics = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    totalRequests: Number(o.totalRequests ?? o.TotalRequests ?? 0) || 0,
    pending: Number(o.pending ?? o.Pending ?? 0) || 0,
    approved: Number(o.approved ?? o.Approved ?? 0) || 0,
    rejected: Number(o.rejected ?? o.Rejected ?? 0) || 0,
    withdrawn: Number(o.withdrawn ?? o.Withdrawn ?? 0) || 0,
  };
};

/** @param {unknown} raw */
const normalizeAttendanceChangeRequestListItem = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const submittedAtUtc = o.submittedAtUtc ?? o.SubmittedAtUtc ?? o.createdAtUtc ?? o.CreatedAtUtc;
  const status = o.status ?? o.Status;
  const { statusLabel, isAwaitingApproval } = deriveChangeRequestListItemUiFields(
    status,
    o.statusLabel ?? o.StatusLabel,
    o.isAwaitingApproval ?? o.IsAwaitingApproval
  );
  return {
    requestId: String(o.id ?? o.Id ?? o.requestId ?? o.RequestId ?? ""),
    userAttendanceEventId: String(o.userAttendanceEventId ?? o.UserAttendanceEventId ?? ""),
    requestedByUserId: String(o.requestedByUserId ?? o.RequestedByUserId ?? ""),
    employeeName: String(o.requestedByName ?? o.RequestedByName ?? o.employeeName ?? o.EmployeeName ?? ""),
    requestedByEmail: String(o.requestedByEmail ?? o.RequestedByEmail ?? ""),
    employeeProfileImageUrl: String(o.employeeProfileImageUrl ?? o.EmployeeProfileImageUrl ?? "").trim() || null,
    employeeOfficeName: String(o.employeeOfficeName ?? o.EmployeeOfficeName ?? ""),
    employeeDepartmentName: String(o.employeeDepartmentName ?? o.EmployeeDepartmentName ?? ""),
    approverUserId: String(o.approverUserId ?? o.ApproverUserId ?? ""),
    approverName: String(o.approverName ?? o.ApproverName ?? ""),
    requestType: Number(o.requestType ?? o.RequestType ?? 0),
    status,
    statusLabel,
    isAwaitingApproval,
    submittedAtUtc,
    createdAtUtc: submittedAtUtc,
    processedAtUtc: o.processedAtUtc ?? o.ProcessedAtUtc,
    approverComment: String(o.approverComment ?? o.ApproverComment ?? ""),
    pendingOccurredAtUtc: o.pendingOccurredAtUtc ?? o.PendingOccurredAtUtc,
    eventOccurredAtUtc: o.eventOccurredAtUtc ?? o.EventOccurredAtUtc,
    eventType: Number(o.eventType ?? o.EventType ?? 0),
    eventWorkplaceType: Number(o.eventWorkplaceType ?? o.EventWorkplaceType ?? 0),
    previousOccurredAtUtc: o.previousOccurredAtUtc ?? o.PreviousOccurredAtUtc,
    previousLocation: o.previousLocation ?? o.PreviousLocation ?? "",
    previousNote: o.previousNote ?? o.PreviousNote ?? "",
    previousWorkplaceType: o.previousWorkplaceType ?? o.PreviousWorkplaceType,
    newOccurredAtUtc: o.newOccurredAtUtc ?? o.NewOccurredAtUtc,
    newLocation: o.newLocation ?? o.NewLocation ?? "",
    newNote: o.newNote ?? o.NewNote ?? "",
    newWorkplaceType: o.newWorkplaceType ?? o.NewWorkplaceType,
    deleteRequestSummary: String(o.deleteRequestSummary ?? o.DeleteRequestSummary ?? ""),
  };
};

/**
 * Approver-queue filters for GET `AttendanceChangeRequests/GetApproverQueuePaged`.
 * @param {object} q
 * @param {number} q.periodMode — 0 Range, 1 Monthly, 2 Weekly
 * @param {string} [q.startDateUtc] [q.endDateUtc] when Range
 * @param {number} [q.year] [q.month] when Monthly (month 1–12)
 * @param {string} [q.weekRangeStart] [q.weekRangeEnd] ISO when Weekly
 */
export const buildApproverChangeRequestsQuery = (q) => {
  if (!q || typeof q !== "object") return {};
  const p = /** @type {Record<string, unknown>} */ ({});
  const mode = Number(q.periodMode ?? 0);
  p.periodMode = mode;
  if (q.status != null && String(q.status).trim() !== "") p.status = String(q.status).trim();
  if (q.employeeSearch != null && String(q.employeeSearch).trim() !== "")
    p.employeeSearch = String(q.employeeSearch).trim();
  if (q.officeId != null && q.officeId !== "" && Number.isFinite(Number(q.officeId))) p.officeId = Number(q.officeId);
  if (q.departmentId != null && q.departmentId !== "" && Number.isFinite(Number(q.departmentId)))
    p.departmentId = Number(q.departmentId);
  if (q.requestTypeFilter != null && String(q.requestTypeFilter).trim() !== "")
    p.requestTypeFilter = String(q.requestTypeFilter).trim();

  if (mode === 0) {
    if (q.startDateUtc) p.startDateUtc = q.startDateUtc;
    if (q.endDateUtc) p.endDateUtc = q.endDateUtc;
  } else if (mode === 1) {
    if (q.year != null) p.year = Number(q.year);
    if (q.month != null) p.month = Number(q.month);
  } else if (mode === 2) {
    if (q.weekRangeStart) p.weekRangeStart = q.weekRangeStart;
    if (q.weekRangeEnd) p.weekRangeEnd = q.weekRangeEnd;
  }
  return p;
};

const omitEmptyParams = (obj) => {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
};

/**
 * GET `AttendanceChangeRequests/ExportApproverQueue/{Excel|Csv|Pdf}` — same query params as approver queue (no pagination).
 * @param {string} path
 * @param {Record<string, unknown>} query
 */
const fetchApproverQueueExportBlob = async (path, query) => {
  const res = await axiosBasic.get(path, {
    params: omitEmptyParams(query),
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertAttendanceOk(json);
    } catch (e) {
      if (e?.isAttendanceEnvelopeError) throw e;
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

/**
 * @param {Record<string, unknown>} approverFilters — same shape as approver queue list filters (see {@link buildApproverChangeRequestsQuery})
 */
export const fetchApproverQueueExportExcel = (approverFilters) =>
  fetchApproverQueueExportBlob(
    AttendanceChangeRequests.ExportApproverQueueExcel,
    buildApproverChangeRequestsQuery(approverFilters)
  );

/** @param {Record<string, unknown>} approverFilters */
export const fetchApproverQueueExportCsv = (approverFilters) =>
  fetchApproverQueueExportBlob(
    AttendanceChangeRequests.ExportApproverQueueCsv,
    buildApproverChangeRequestsQuery(approverFilters)
  );

/** @param {Record<string, unknown>} approverFilters */
export const fetchApproverQueueExportPdf = (approverFilters) =>
  fetchApproverQueueExportBlob(
    AttendanceChangeRequests.ExportApproverQueuePdf,
    buildApproverChangeRequestsQuery(approverFilters)
  );

const normalizeApproverChangeRequestsPaged = (d, fallbackPage, fallbackPageSize) => {
  const itemsRaw = d.items ?? d.Items ?? [];
  const items = Array.isArray(itemsRaw)
    ? itemsRaw.map(normalizeAttendanceChangeRequestListItem).filter(Boolean)
    : [];
  const statsRaw = d.statistics ?? d.Statistics;
  return {
    page: Number(d.page ?? d.Page ?? fallbackPage) || fallbackPage,
    pageSize: Number(d.pageSize ?? d.PageSize ?? fallbackPageSize) || fallbackPageSize,
    totalCount: Number(d.totalCount ?? d.TotalCount ?? 0) || 0,
    appliedPeriodMode: d.appliedPeriodMode ?? d.AppliedPeriodMode,
    effectiveSubmittedFromUtc: d.effectiveSubmittedFromUtc ?? d.EffectiveSubmittedFromUtc ?? null,
    effectiveSubmittedToUtc: d.effectiveSubmittedToUtc ?? d.EffectiveSubmittedToUtc ?? null,
    filterYear: d.filterYear ?? d.FilterYear ?? null,
    filterMonth: d.filterMonth ?? d.FilterMonth ?? null,
    statistics: normalizeAttendanceChangeRequestPageStatistics(statsRaw),
    items,
  };
};

const normalizeLegacyChangeRequestsPaged = (d, page, pageSize) => {
  const itemsRaw = d.items ?? d.Items ?? [];
  const items = Array.isArray(itemsRaw)
    ? itemsRaw.map(normalizeAttendanceChangeRequestListItem).filter(Boolean)
    : [];
  return {
    page: Number(d.page ?? d.Page ?? page) || page,
    pageSize: Number(d.pageSize ?? d.PageSize ?? pageSize) || pageSize,
    totalCount: Number(d.totalCount ?? d.TotalCount ?? 0) || 0,
    appliedPeriodMode: null,
    effectiveSubmittedFromUtc: null,
    effectiveSubmittedToUtc: null,
    filterYear: null,
    filterMonth: null,
    statistics: null,
    items,
  };
};

/**
 * @param {number} page
 * @param {number} pageSize
 * @param {string|undefined|null|Record<string, unknown>} third — legacy: optional `status` string; object: approver filters via {@link buildApproverChangeRequestsQuery}. For the current user&apos;s submissions use {@link fetchMySubmissionsPaged}.
 */
/**
 * @param {unknown} raw — `AttendanceChangeRequestByEventDto`
 * @returns {{ userAttendanceEventId: string, requests: ReturnType<typeof normalizeAttendanceChangeRequestListItem>[] }}
 */
const normalizeChangeRequestsByEvent = (raw) => {
  if (!raw || typeof raw !== "object") return { userAttendanceEventId: "", requests: [] };
  const o = /** @type {Record<string, unknown>} */ (raw);
  const userAttendanceEventId = String(o.userAttendanceEventId ?? o.UserAttendanceEventId ?? "");
  const reqRaw = o.requests ?? o.Requests ?? [];
  const requests = Array.isArray(reqRaw)
    ? reqRaw.map(normalizeAttendanceChangeRequestListItem).filter(Boolean)
    : [];
  return { userAttendanceEventId, requests };
};

/**
 * GET `AttendanceChangeRequests/ByEvent/{userAttendanceEventId}` — change request(s) for one attendance event.
 * By default the API returns only the **latest** row when the same event id has multiple DB rows (e.g. withdraw + resubmit).
 * Pass `{ includeAllHistory: true }` for every row, newest first.
 * @param {string} userAttendanceEventId
 * @param {{ includeAllHistory?: boolean }} [options]
 */
export const fetchChangeRequestsByEvent = async (userAttendanceEventId, options) => {
  const id = String(userAttendanceEventId ?? "").trim();
  if (!id) return null;
  const params = {};
  if (options?.includeAllHistory === true) {
    params.includeAllHistory = true;
  }
  const res = await axiosBasic.get(`${AttendanceChangeRequests.ByEvent}${encodeURIComponent(id)}`, { params });
  assertAttendanceOk(res.data);
  const inner = unwrapInner(res.data);
  if (inner == null) return { userAttendanceEventId: id, requests: [] };
  return normalizeChangeRequestsByEvent(inner);
};

export const fetchChangeRequestsPaged = async (page, pageSize, third) => {
  const params = { page, pageSize };
  if (third == null || typeof third === "string") {
    if (third) params.status = third;
    const res = await axiosBasic.get(AttendanceChangeRequests.GetApproverQueuePaged, { params });
    assertAttendanceOk(res.data);
    const d = unwrapInner(res.data) ?? {};
    return normalizeLegacyChangeRequestsPaged(d, page, pageSize);
  }
  Object.assign(params, buildApproverChangeRequestsQuery(third));
  const res = await axiosBasic.get(AttendanceChangeRequests.GetApproverQueuePaged, { params });
  assertAttendanceOk(res.data);
  const d = unwrapInner(res.data) ?? {};
  return normalizeApproverChangeRequestsPaged(d, page, pageSize);
};

/**
 * GET `AttendanceChangeRequests/MySubmissions` — current user's submissions; same query shape as the approver queue list filters.
 * @param {number} page
 * @param {number} pageSize
 * @param {Record<string, unknown>} query — {@link buildApproverChangeRequestsQuery}
 */
export const fetchMySubmissionsPaged = async (page, pageSize, query) => {
  const params = { page, pageSize, ...omitEmptyParams(buildApproverChangeRequestsQuery(query)) };
  const res = await axiosBasic.get(AttendanceChangeRequests.MySubmissions, { params });
  assertAttendanceOk(res.data);
  const d = unwrapInner(res.data) ?? {};
  return normalizeApproverChangeRequestsPaged(d, page, pageSize);
};

export const approveChangeRequest = async (requestId, comment) => {
  const res = await axiosBasic.post(AttendanceChangeRequests.Approve, {
    RequestId: String(requestId ?? "").trim(),
    Comment: comment != null ? String(comment) : "",
  });
  assertAttendanceOk(res.data);
  const inner = unwrapInner(res.data);
  return inner ? normalizeUserAttendanceEvent(inner) : null;
};

export const rejectChangeRequest = async (requestId, comment) => {
  const res = await axiosBasic.post(AttendanceChangeRequests.Reject, {
    RequestId: String(requestId ?? "").trim(),
    Comment: comment != null ? String(comment) : "",
  });
  assertAttendanceOk(res.data);
  return unwrapInner(res.data);
};

/**
 * Withdraw a pending change request (submitter only). POST body matches typical `RequestId` DTOs.
 * @param {string} requestId
 */
export const withdrawChangeRequest = async (requestId) => {
  const res = await axiosBasic.post(AttendanceChangeRequests.Withdraw, {
    RequestId: String(requestId ?? "").trim(),
  });
  assertAttendanceOk(res.data);
  return unwrapInner(res.data);
};

/**
 * GET `AttendanceChangeRequests/Stats` — counts by status (approver queue or own submissions).
 * @param {boolean} [forMe] — `true` matches scope of GetMySubmissions; default `false` is approver queue (same as GetApproverQueuePaged).
 * @returns {Promise<{ total: number, pending: number, approved: number, rejected: number, withdrawn: number }>}
 */
export const fetchChangeRequestStats = async (forMe = false) => {
  const res = await axiosBasic.get(AttendanceChangeRequests.Stats, {
    params: { forMe: Boolean(forMe) },
  });
  assertAttendanceOk(res.data);
  const d = unwrapInner(res.data) ?? {};
  return {
    total: Number(d.total ?? d.Total ?? 0) || 0,
    pending: Number(d.pending ?? d.Pending ?? 0) || 0,
    approved: Number(d.approved ?? d.Approved ?? 0) || 0,
    rejected: Number(d.rejected ?? d.Rejected ?? 0) || 0,
    withdrawn: Number(d.withdrawn ?? d.Withdrawn ?? 0) || 0,
  };
};

export const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// —— Company reporting (Attendance/Reporting/*) ——

/**
 * @param {object} opts
 * @param {number} opts.periodMode — {@link AttendanceReportingPeriodMode}
 * @param {Date} [opts.dayUtc]
 * @param {Date} [opts.weekRangeStart]
 * @param {Date} [opts.weekRangeEnd]
 * @param {number} [opts.filterYear]
 * @param {number} [opts.filterMonth]
 * @param {string|number} [opts.officeId]
 * @param {string|number} [opts.departmentId]
 * @param {string|number} [opts.workplaceType] — 0 Office, 1 Remote, 2 FieldVisit (`int?` on API)
 * @param {string} [opts.statusFilter] — Today only; matches API StatusFilter (comma-separated OR allowed server-side).
 * @param {string} [opts.search] — Substring on employee name or email (today/range/export; omit for Stats).
 * @param {number} [opts.page]
 * @param {number} [opts.pageSize]
 */
export const buildAttendanceReportingQuery = (opts) => {
  if (!opts || typeof opts !== "object") return {};
  const p = /** @type {Record<string, unknown>} */ ({});
  const mode = Number(opts.periodMode ?? AttendanceReportingPeriodMode.Today) || 0;
  // Short-backed enum: query must be string (e.g. "0"), not a JSON number (binds as Int32).
  p.PeriodMode = String(mode);
  p.page = Number(opts.page ?? 1) || 1;
  p.pageSize = Number(opts.pageSize ?? 20) || 20;

  if (opts.officeId != null && opts.officeId !== "" && Number.isFinite(Number(opts.officeId))) p.officeId = Number(opts.officeId);
  if (opts.departmentId != null && opts.departmentId !== "" && Number.isFinite(Number(opts.departmentId)))
    p.departmentId = Number(opts.departmentId);

  // API: int? WorkplaceType — 0 Office, 1 Remote, 2 FieldVisit (allow 0; avoid truthy-only checks).
  const wtIn = opts.workplaceType ?? opts.WorkplaceType;
  if (wtIn !== undefined && wtIn !== null && wtIn !== "") {
    const n = Number(wtIn);
    if (Number.isFinite(n) && [0, 1, 2].includes(n)) {
      p.WorkplaceType = n;
    }
  }

  if (mode === AttendanceReportingPeriodMode.Today) {
    if (opts.dayUtc instanceof Date && !Number.isNaN(opts.dayUtc.getTime())) p.dayUtc = toUtcDateOnlyISO(opts.dayUtc);
    const sf = opts.statusFilter != null ? String(opts.statusFilter).trim() : "";
    if (sf) p.StatusFilter = sf;
  } else if (mode === AttendanceReportingPeriodMode.Weekly) {
    if (opts.weekRangeStart instanceof Date && !Number.isNaN(opts.weekRangeStart.getTime()))
      p.weekRangeStartUtc = toUtcDateOnlyISO(opts.weekRangeStart);
    if (opts.weekRangeEnd instanceof Date && !Number.isNaN(opts.weekRangeEnd.getTime()))
      p.weekRangeEndUtc = toUtcDateOnlyISO(opts.weekRangeEnd);
  } else if (mode === AttendanceReportingPeriodMode.Monthly) {
    if (opts.filterYear != null) p.filterYear = Number(opts.filterYear);
    if (opts.filterMonth != null) p.filterMonth = Number(opts.filterMonth);
  }

  const searchQ = opts.search != null ? String(opts.search).trim() : "";
  if (searchQ) p.Search = searchQ;

  return p;
};

/** @param {unknown} raw */
const normalizeAttendanceReportingStats = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const lp = o.latePercentage ?? o.LatePercentage;
  const ar = o.averageAttendanceRatePercent ?? o.AverageAttendanceRatePercent;
  return {
    periodMode: o.periodMode ?? o.PeriodMode,
    fromUtc: o.fromUtc ?? o.FromUtc ?? null,
    toUtc: o.toUtc ?? o.ToUtc ?? null,
    totalPresent: Number(o.totalPresent ?? o.TotalPresent ?? 0) || 0,
    latePercentage: lp != null && lp !== "" && Number.isFinite(Number(lp)) ? Number(lp) : null,
    averageAttendanceRatePercent: ar != null && ar !== "" && Number.isFinite(Number(ar)) ? Number(ar) : null,
  };
};

/** @param {unknown} raw */
const normalizeReportingTodayRow = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    userId: String(o.userId ?? o.UserId ?? ""),
    employeeName: String(o.employeeName ?? o.EmployeeName ?? o.userName ?? o.UserName ?? ""),
    email: String(o.email ?? o.Email ?? ""),
    designation: String(o.designation ?? o.Designation ?? ""),
    imageUrl: (() => {
      const u =
        o.imageUrl ??
        o.ImageUrl ??
        o.profileImageUrl ??
        o.ProfileImageUrl ??
        o.employeeProfileImageUrl ??
        o.EmployeeProfileImageUrl ??
        null;
      if (u == null) return null;
      const s = String(u).trim();
      return s === "" ? null : s;
    })(),
    departmentName: String(o.departmentName ?? o.DepartmentName ?? ""),
    officeName: String(o.officeName ?? o.OfficeName ?? ""),
    checkInUtc: o.checkInUtc ?? o.CheckInUtc ?? o.firstCheckInUtc ?? o.FirstCheckInUtc ?? null,
    checkOutUtc: o.checkOutUtc ?? o.CheckOutUtc ?? o.lastCheckOutUtc ?? o.LastCheckOutUtc ?? null,
    workHours: o.workHours ?? o.WorkHours ?? o.hoursWorked ?? o.HoursWorked ?? null,
    statusLabel: String(
      o.statusLabel ?? o.StatusLabel ?? o.status ?? o.Status ?? ""
    ).trim(),
    /** @type {number | null} First check-in workplace type; null when Absent. */
    workplaceType: (() => {
      const w = o.workplaceType ?? o.WorkplaceType;
      if (w == null || w === "") return null;
      const n = Number(w);
      return Number.isFinite(n) ? n : null;
    })(),
  };
};

const normalizeReportingTodayPaged = (raw, fallbackPage, fallbackPageSize) => {
  const d = raw && typeof raw === "object" ? /** @type {Record<string, unknown>} */ (raw) : {};
  const itemsRaw = d.items ?? d.Items ?? [];
  const items = Array.isArray(itemsRaw) ? itemsRaw.map(normalizeReportingTodayRow).filter(Boolean) : [];
  return {
    page: Number(d.page ?? d.Page ?? fallbackPage) || fallbackPage,
    pageSize: Number(d.pageSize ?? d.PageSize ?? fallbackPageSize) || fallbackPageSize,
    totalCount: Number(d.totalCount ?? d.TotalCount ?? 0) || 0,
    periodMode: d.periodMode ?? d.PeriodMode,
    fromUtc: d.fromUtc ?? d.FromUtc ?? null,
    toUtc: d.toUtc ?? d.ToUtc ?? null,
    items,
  };
};

/** @param {unknown} dateUtc — API UTC calendar day */
const calendarWeekdayShortUtc = (dateUtc) => {
  if (dateUtc == null) return "";
  const d = new Date(/** @type {string | number | Date} */ (dateUtc));
  if (Number.isNaN(d.getTime())) return "";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return days[d.getUTCDay()] ?? "";
};

/** @param {unknown} raw */
const normalizeCalendarDayColumn = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const dateUtc = o.dateUtc ?? o.DateUtc ?? null;
  return {
    dateUtc,
    label: String(o.label ?? o.Label ?? "").trim() || "—",
    weekdayShort: calendarWeekdayShortUtc(dateUtc),
  };
};

/**
 * @param {unknown} raw
 * @param {number} columnCount — align `Statuses` to `Columns` length
 */
const normalizeReportingRangeRow = (raw, columnCount) => {
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const statusesRaw = o.statuses ?? o.Statuses;
  let statuses = Array.isArray(statusesRaw)
    ? statusesRaw.map((x) => {
        const n = Number(x);
        return n === 0 || n === 1 || n === 2 ? n : 0;
      })
    : [];
  const n = Number(columnCount) || 0;
  if (n > 0) {
    if (statuses.length < n) {
      statuses = statuses.concat(Array(n - statuses.length).fill(0));
    } else if (statuses.length > n) {
      statuses = statuses.slice(0, n);
    }
  }
  return {
    userId: String(o.userId ?? o.UserId ?? ""),
    employeeName: String(o.employeeName ?? o.EmployeeName ?? ""),
    designation: String(o.designation ?? o.Designation ?? ""),
    imageUrl: (() => {
      const u =
        o.imageUrl ??
        o.ImageUrl ??
        o.profileImageUrl ??
        o.ProfileImageUrl ??
        o.employeeProfileImageUrl ??
        o.EmployeeProfileImageUrl ??
        null;
      if (u == null) return null;
      const s = String(u).trim();
      return s === "" ? null : s;
    })(),
    email: String(o.email ?? o.Email ?? ""),
    departmentName: String(o.departmentName ?? o.DepartmentName ?? ""),
    officeName: String(o.officeName ?? o.OfficeName ?? ""),
    totalWorkHoursCompleted: Number(o.totalWorkHoursCompleted ?? o.TotalWorkHoursCompleted ?? 0) || 0,
    daysWithAttendance: Number(o.daysWithAttendance ?? o.DaysWithAttendance ?? 0) || 0,
    missingCheckInCount: Number(o.missingCheckInCount ?? o.MissingCheckInCount ?? 0) || 0,
    missingCheckOutCount: Number(o.missingCheckOutCount ?? o.MissingCheckOutCount ?? 0) || 0,
    statuses,
    missedCheckedInCount: Number(o.missedCheckedInCount ?? o.MissedCheckedInCount ?? 0) || 0,
  };
};

const normalizeReportingRangePaged = (raw, fallbackPage, fallbackPageSize) => {
  const d = raw && typeof raw === "object" ? /** @type {Record<string, unknown>} */ (raw) : {};
  const colsRaw = d.columns ?? d.Columns ?? [];
  const columns = Array.isArray(colsRaw) ? colsRaw.map(normalizeCalendarDayColumn).filter(Boolean) : [];
  const nCol = columns.length;
  const itemsRaw = d.items ?? d.Items ?? [];
  const items = Array.isArray(itemsRaw) ? itemsRaw.map((row) => normalizeReportingRangeRow(row, nCol)).filter(Boolean) : [];
  return {
    page: Number(d.page ?? d.Page ?? fallbackPage) || fallbackPage,
    pageSize: Number(d.pageSize ?? d.PageSize ?? fallbackPageSize) || fallbackPageSize,
    totalCount: Number(d.totalCount ?? d.TotalCount ?? 0) || 0,
    periodMode: d.periodMode ?? d.PeriodMode,
    fromUtc: d.fromUtc ?? d.FromUtc ?? null,
    toUtc: d.toUtc ?? d.ToUtc ?? null,
    columns,
    items,
  };
};

/**
 * @param {Parameters<typeof buildAttendanceReportingQuery>[0]} opts
 */
export const fetchAttendanceReportingStats = async (opts) => {
  const params = omitEmptyParams(buildAttendanceReportingQuery({ ...opts, page: opts.page ?? 1, pageSize: opts.pageSize ?? 20 }));
  const res = await axiosBasic.get(Attendance.ReportingStats, { params });
  assertAttendanceOk(res.data);
  return normalizeAttendanceReportingStats(unwrapInner(res.data));
};

/**
 * @param {Parameters<typeof buildAttendanceReportingQuery>[0]} opts
 */
export const fetchAttendanceReportingTodayPaged = async (opts) => {
  const params = omitEmptyParams(buildAttendanceReportingQuery(opts));
  const res = await axiosBasic.get(Attendance.ReportingToday, { params });
  assertAttendanceOk(res.data);
  const inner = unwrapInner(res.data) ?? {};
  const page = Number(opts.page ?? 1) || 1;
  const pageSize = Number(opts.pageSize ?? 20) || 20;
  return normalizeReportingTodayPaged(inner, page, pageSize);
};

/**
 * @param {Parameters<typeof buildAttendanceReportingQuery>[0]} opts
 */
export const fetchAttendanceReportingRangePaged = async (opts) => {
  const params = omitEmptyParams(buildAttendanceReportingQuery(opts));
  const res = await axiosBasic.get(Attendance.ReportingRange, { params });
  assertAttendanceOk(res.data);
  const inner = unwrapInner(res.data) ?? {};
  const page = Number(opts.page ?? 1) || 1;
  const pageSize = Number(opts.pageSize ?? 20) || 20;
  return normalizeReportingRangePaged(inner, page, pageSize);
};

const fetchAttendanceReportingExportBlob = async (format, opts) => {
  const base = omitEmptyParams(buildAttendanceReportingQuery(opts));
  const fmt = format === "pdf" ? "pdf" : format === "csv" ? "csv" : "xlsx";
  const res = await axiosBasic.get(Attendance.ReportingExport, {
    params: { ...base, format: fmt },
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertAttendanceOk(json);
    } catch (e) {
      if (e?.isAttendanceEnvelopeError) throw e;
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

/**
 * @param {"xlsx"|"csv"|"pdf"} format
 * @param {Parameters<typeof buildAttendanceReportingQuery>[0]} opts
 */
export const fetchAttendanceReportingExport = (format, opts) => fetchAttendanceReportingExportBlob(format, opts);

/**
 * Maps the browser session to AttendanceClientDeviceType (mobile, tablet, desktop, or web).
 * Tablet is detected before phone; desktop browsers return Desktop; unknown embeds → Web.
 */
export const detectClientDeviceType = () => {
  if (typeof navigator === "undefined") return AttendanceClientDeviceType.Unknown;

  const ua = navigator.userAgent || "";

  const isIPad =
    /iPad/i.test(ua) ||
    (navigator.platform === "MacIntel" &&
      typeof navigator.maxTouchPoints === "number" &&
      navigator.maxTouchPoints > 1);

  const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
  const isTablet =
    isIPad ||
    isAndroidTablet ||
    /Tablet|PlayBook|Kindle|Silk(?!.*Mobile)|\bSM-T\d|\bTab[\s_]/i.test(ua);

  if (isTablet) return AttendanceClientDeviceType.Tablet;

  const isMobile =
    /Mobi|Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|webOS/i.test(ua) ||
    (/Android/i.test(ua) && /Mobile/i.test(ua));

  if (isMobile) return AttendanceClientDeviceType.Mobile;

  if (/Windows NT|Macintosh|X11|Linux x86_64|Linux x86|CrOS|Linux armv8l/i.test(ua)) {
    return AttendanceClientDeviceType.Desktop;
  }

  return AttendanceClientDeviceType.Web;
};
